import { z } from 'zod';

import { makeEndpoint } from '../common';

export const DynamicPricingEntityTypeSchema = z.enum(['experience', 'resource_group']);
export type DynamicPricingEntityType = z.infer<typeof DynamicPricingEntityTypeSchema>;

export const DynamicPricingLimitSchema = z
  .object({
    priceTierName: z.string().trim().min(1),
    floor: z.number().min(0),
    ceiling: z.number().min(0),
  })
  .refine(({ floor, ceiling }) => floor <= ceiling, { message: 'The floor must be less than or equal to the ceiling.' });
export type DynamicPricingLimit = z.infer<typeof DynamicPricingLimitSchema>;

export const DynamicPricingRuleLimitsSchema = z.object({
  ruleId: z.string().uuid(),
  limits: z.array(DynamicPricingLimitSchema).min(1),
});
export type DynamicPricingRuleLimits = z.infer<typeof DynamicPricingRuleLimitsSchema>;

/** The listing default limits plus optional per-rule replacements, keyed by the rule's draft (latest) id. */
export const DynamicPricingLimitsSchema = z.object({
  default: z.array(DynamicPricingLimitSchema),
  rules: z.array(DynamicPricingRuleLimitsSchema),
});
export type DynamicPricingLimits = z.infer<typeof DynamicPricingLimitsSchema>;

export const DynamicPricingEngineResponseSchema = z.object({
  brandId: z.string().uuid(),
  entityType: DynamicPricingEntityTypeSchema,
  entityId: z.string().uuid(),
  enabled: z.boolean(),
  enabledBy: z.string().nullable(),
  enabledAt: z.string().nullable(),
  lastSweptAt: z.string().nullable(),
  limits: DynamicPricingLimitsSchema,
});
export type DynamicPricingEngineResponse = z.infer<typeof DynamicPricingEngineResponseSchema>;

/** Enabling requires limits (sent here or already saved); disabling keeps them. */
export const UpdateDynamicPricingEngineRequestSchema = z.object({
  enabled: z.boolean(),
  limits: DynamicPricingLimitsSchema.optional(),
});
export type UpdateDynamicPricingEngineRequest = z.infer<typeof UpdateDynamicPricingEngineRequestSchema>;

export const SaveDynamicPricingLimitsRequestSchema = DynamicPricingLimitsSchema;
export type SaveDynamicPricingLimitsRequest = z.infer<typeof SaveDynamicPricingLimitsRequestSchema>;

type EngineUrlParams = { brandId: string; entityType: DynamicPricingEntityType; entityId: string };
const engineUrl = ({ brandId, entityType, entityId }: EngineUrlParams) =>
  `/v1/brands/${brandId}/dynamic-pricing/${entityType}/${entityId}`;

export const GetDynamicPricingEngineEndpoint = makeEndpoint({
  method: 'GET',
  url: engineUrl,
  requestSchema: z.object({}),
  responseSchema: DynamicPricingEngineResponseSchema,
});

export const UpdateDynamicPricingEngineEndpoint = makeEndpoint({
  method: 'PATCH',
  url: engineUrl,
  requestSchema: UpdateDynamicPricingEngineRequestSchema,
  responseSchema: DynamicPricingEngineResponseSchema,
});

export const SaveDynamicPricingLimitsEndpoint = makeEndpoint({
  method: 'PUT',
  url: (params: EngineUrlParams) => `${engineUrl(params)}/limits`,
  requestSchema: SaveDynamicPricingLimitsRequestSchema,
  responseSchema: DynamicPricingEngineResponseSchema,
});
